/**
 * Le moteur de la partition : des couches, un recyclage, et une seule écriture
 * par image.
 *
 * ## Ce qu'il remplace, et pourquoi il fallait le remplacer
 *
 * Le rail était un arbre React de plusieurs centaines de nœuds, redessiné à
 * chaque **temps** — la tête de lecture se promouvait en état à chaque borne —
 * et translaté par une écriture de style à chaque **image**. Les deux coûts
 * étaient réels et distincts :
 *
 * - à l'image, le ruban était peint et non composé. Il vivait sous un
 *   `mask-image`, et un masque force son sous-arbre à être re-rastérisé quand
 *   son contenu bouge : le navigateur repeignait donc toute la partition
 *   soixante fois par seconde. C'est ce que « paint flashing » montrait, le
 *   cadre entier vert en permanence ;
 * - au temps, le rendu React reparcourait la frise entière avant de jeter ce
 *   qui n'était pas visible, et chaque nom mesuré forçait un calcul de mise en
 *   page. La pointe tombait exactement sur le temps.
 *
 * Ce module supprime les deux. Le masque devient un **rognage** plus deux
 * dégradés posés dans une couche immobile ; le ruban devient une couche
 * composée, promue une fois, que l'on ne fait que translater ; et le dessin
 * devient un recyclage de nœuds piloté par une fenêtre, dont le coût est celui
 * de ce qui **entre et sort**, jamais celui du niveau.
 *
 * ## Les cinq couches, et ce que chacune a le droit de faire
 *
 * 1. **Les portées** — les dix lignes, pleine largeur. Immobiles, peintes une
 *    fois au montage. Elles appartiennent au cadre et non au ruban : une portée
 *    qui n'existerait que là où le morceau donne des notes ne serait plus une
 *    échelle, et la moitié gauche du rail serait un trou à l'ouverture du
 *    niveau.
 * 2. **Le ruban** — tout ce qui porte un temps, dans les teintes de ce qui n'est
 *    pas encore joué. Promu (`will-change`), translaté en `translate3d`, jamais
 *    repeint tant que la fenêtre ne bouge pas.
 * 3. **Le ruban allumé** — les mêmes notes, dans les teintes de ce qui est
 *    joué, découpées à la tête de lecture. Voir ci-dessous.
 * 4. **La tête** — le trait, les noms qui lui sont collés, les lueurs des
 *    touches tenues. Immobile en abscisse ; seuls les noms collés reçoivent une
 *    translation, et pour la même raison que le ruban.
 * 5. **Les fondus** — deux dégradés aux bords, posés par-dessus tout le reste.
 *    Ils remplacent le masque au pixel près : le fond derrière le rail est
 *    exactement `--color-bg-deep` à cette hauteur — la lueur de la scène est
 *    déjà nulle en bas de sa boîte — donc peindre le fond par-dessus et rendre
 *    le contenu transparent donnent la même image. La différence est qu'un
 *    dégradé posé dans une couche immobile est peint **une fois**.
 *
 * ## L'allumage est une géométrie, et non un changement de couleur
 *
 * C'est le point qui a demandé de tout remesurer, et celui qui décide de
 * l'affaire.
 *
 * Une couche composée est gardée en texture : la déplacer est gratuit, mais
 * **en changer un pixel invalide toute la couche**. Une note qui passait de sa
 * teinte d'attente à sa teinte de jeu déclarait donc un dégât de la taille du
 * ruban entier — neuf mille pixels de large pour un corps de vingt-six. À une
 * note par croche, plus le fondu de cent quarante millisecondes qui la suivait,
 * cela faisait une repeinture pleine couche en permanence : le cadre restait
 * vert d'un bout à l'autre sous « paint flashing », alors même que la
 * translation, elle, ne coûtait plus rien.
 *
 * Rien ne change donc plus de couleur. Les notes sont gravées **deux fois** :
 * une fois en teinte d'attente dans le ruban, une fois en teinte de jeu dans un
 * second ruban posé par-dessus, lui-même dans une boîte qui s'arrête à la tête
 * de lecture. Les deux rubans reçoivent la même matrice à chaque image, donc
 * les deux exemplaires d'une note tombent exactement l'un sur l'autre ; ce qui
 * est derrière la tête se lit dans la teinte de jeu, ce qui est devant dans
 * celle d'attente.
 *
 * La boîte de découpe ne bouge jamais, et c'est ce qui rend le tour possible :
 * la tête est clouée au premier quart, donc « derrière la tête » est une
 * constante de mise en page et non un état à tenir à jour. Il n'y a plus ni
 * curseur d'allumage, ni fondu, ni écriture de style pendant que la musique
 * joue.
 *
 * Le prix est deux fois plus de nœuds pour les notes, et un dessin qui change :
 * une note se **remplit** au passage de la tête au lieu de s'allumer d'un bloc
 * à son attaque. C'est plus proche de ce que l'écran veut dire — « voilà ce qui
 * est derrière, voilà ce qui vient » — et c'est littéralement le remplissage de
 * la barre du temps que cet allumage remplaçait.
 *
 * Le rognage horizontal, lui, est un `overflow: clip` : c'est un nœud de
 * découpe dans l'arbre de propriétés du compositeur, ce qu'un masque n'est pas.
 * Il coûte zéro par image, et il ne crée pas de conteneur de défilement.
 *
 * ## Rien ne se lit pendant une image
 *
 * Aucune mesure de mise en page (`offsetWidth`, `getBoundingClientRect`) n'est
 * faite dans `frame`. Les seules mesures du moteur sont celles des noms, qui
 * décident de leur troncature, et elles ont lieu au changement de fenêtre, en
 * **deux passes** : on écrit tout, puis on lit tout. Une lecture entrelacée
 * avec des écritures force un calcul de mise en page par nom.
 *
 * ## Il n'ouvre aucune boucle
 *
 * `frame` est appelée depuis le ticker Pixi du roll, par `useStudioTick`.
 * C'est la même boucle qui dessine les notes, gouvernée par le rendu à la
 * demande : elle tourne quand le transport joue, quand un geste de navigation
 * est en cours, et elle se parke sinon. Une vue qui ouvrirait sa propre
 * `requestAnimationFrame` rallumerait exactement ce que ce gouverneur éteint.
 */

import { getFpsWatchdog } from "@/lib/perf/fps-watchdog";
import { FONT_MONO, TEXT_MUTE, VIOLET_BRIGHT } from "@/components/ui/organisms/chord-practice/palette";
import { spellStaffKey } from "@/lib/harmony/staff";
import { shiftAt, writtenStep } from "@/lib/harmony/ottava";
import type { FriezeSegment } from "@/lib/harmony/frieze";
import {
  BASS_NAME_PX,
  CHORD_BLOCK_PX,
  CHORD_NAME_PX,
  CHORD_NOTES_PX,
  CHORD_POSITION_PX,
  HEAD_AT,
  HEAD_LINE,
  HEAD_TIP,
  INK_MAX,
  LEFT_HAND_COLOR,
  LOOP_SIZE_PX,
  NAME_OFFSET_PX,
  NAME_TAIL_PX,
  OTTAVA_DASH_PX,
  OTTAVA_GAP_PX,
  RIGHT_HAND_COLOR,
  SEPARATOR,
  STAFF_LINE,
  grey,
  itemsIn,
  stepMiddle,
  staveForStep,
  type LabelItem,
  type NoteItem,
  type ScoreItem,
  type ScoreLayout,
  type SignItem,
  type StavePlace,
} from "./score-layout";

/**
 * Ce qu'on grave de part et d'autre de la tête, au plus.
 *
 * La frise porte tout le niveau, ce qui peut faire des milliers de notes ; le
 * ruban visible, lui, ne fait jamais plus que la largeur de l'écran. On borne
 * donc ce qui entre dans le document, généreusement, plutôt que de mesurer la
 * boîte — ce qui demanderait un observateur et une image de plus à chaque
 * changement de taille.
 *
 * Deux bornes et non une demi-largeur, puisque la tête est au premier quart :
 * il y a beaucoup plus à graver devant elle que derrière.
 *
 * Elles bornent aussi la **texture** de la couche composée, ce qui est la
 * seconde raison d'y tenir : un ruban promu qui porterait tout le niveau
 * demanderait au compositeur une image de plusieurs dizaines de milliers de
 * pixels de large.
 */
const BEHIND_VIEW_PX = 900;
const AHEAD_VIEW_PX = 2400;

/**
 * De combien la tête doit avoir bougé pour qu'on refasse la fenêtre.
 *
 * C'est l'hystérésis, et c'est elle qui décide du rythme du seul travail cher
 * du moteur. La fenêtre est gravée avec cette marge de rab de chaque côté :
 * tant que la tête reste dedans, ce qu'il faut afficher est déjà dans le
 * document, et il n'y a rien à faire.
 *
 * Quatre cents pixels valent presque quatre temps : à cent vingt à la noire, la
 * fenêtre se refait donc toutes les deux secondes environ, au lieu de deux fois
 * par seconde comme les rendus qu'elle remplace.
 */
const REFLOW_PX = 400;

/**
 * La même distance, quand la machine peine.
 *
 * Le seul réglage de qualité du moteur, et il ne touche pas à l'image : il
 * espace les recompositions au lieu d'appauvrir le dessin. Une partition dont
 * on retirerait des objets mentirait ; une partition qui prépare son ruban un
 * peu plus rarement ne se voit pas.
 */
const REFLOW_JANKY_PX = 1200;

/** En deçà, la tête n'a pas bougé assez pour qu'une écriture serve à quelque chose. */
const MOVE_EPSILON_PX = 0.05;

/**
 * La lueur blanche posée sur la touche qu'on tient, à l'aplomb de la tête.
 *
 * Blanche et non de la couleur de sa main : c'est la seule marque de l'écran
 * qui parle de l'instant et non de la main, et elle partage cette blancheur
 * avec la tête de lecture, qui parle de l'instant elle aussi.
 *
 * Une lueur et non une pastille : un disque plein a un bord, et un bord est un
 * objet de plus dans un dessin qui en a déjà beaucoup. Deux fois la note et pas
 * davantage — un degré fait sept pixels, une tierce en fait quatorze, et une
 * lueur plus large déborderait sur la note du dessus.
 */
const SOUND_DOT_SPREAD = 2;

/**
 * La largeur de la colonne des lueurs, en pixels.
 *
 * Généreuse : elle ne dépend que du plafond de la taille du degré, et une
 * colonne trop large ne coûte que sa surface, tandis qu'une colonne trop étroite
 * couperait une lueur.
 */
const GLOW_COLUMN_PX = 48;

/**
 * Le cœur plein, sa dissolution, et le bord où il n'y a plus rien.
 *
 * `closest-side` et non le défaut : un dégradé s'arrête par défaut au **coin**
 * de sa boîte, donc il vaut encore quelque chose au milieu des côtés, et la
 * boîte se voit. Arrêté au côté le plus proche, il est nul partout où elle
 * finit.
 */
const SOUND_DOT_LIGHT =
  "radial-gradient(circle closest-side," +
  " rgba(255, 255, 255, 0.72) 0%," +
  " rgba(255, 255, 255, 0.44) 38%," +
  " rgba(255, 255, 255, 0.16) 68%," +
  " rgba(255, 255, 255, 0) 100%)";

/**
 * La tête de lecture : un cheveu, et deux pointes qui la retrouvent.
 *
 * Deux pixels, là où le rail lui en donnait quatre. Elle traverse une
 * partition, faite de lignes, et une barre épaisse y **efface** la musique sur
 * toute la hauteur au lieu de la marquer. Ce sont les deux pointes, en haut et
 * en bas, qui la rendent repérable d'un coup d'œil : elles sont hors des
 * portées, donc elles ne couvrent rien.
 */
const HEAD_WIDTH_PX = 2;
const HEAD_TIP_PX = 6;

/**
 * Le fondu du bord droit, et pourquoi il n'est pas un rognage.
 *
 * Ce qui vient se dissout au lieu d'être coupé net : une coupe franche à
 * l'endroit exact où le regard va chercher la suite se lit comme une fin. Le
 * masque tient dans le **cadre**, jamais sur le ruban, sinon il défilerait avec
 * lui et se répéterait tous les mille quatre cents pixels.
 */
const FADE_MASK = "linear-gradient(90deg, transparent 0, #000 4%, #000 88%, transparent 100%)";

/** Le fondu des lignes : à droite seulement, la marge des clés reste pleine. */
const STAFF_FADE_MASK = "linear-gradient(90deg, #000 0, #000 90%, transparent 100%)";

/** L'état d'un nom vis-à-vis de la tête de lecture. */
export type LabelState = "ahead" | "stuck" | "passed";

/** Un nom collé à la tête, tel que la boucle a besoin de le connaître. */
type Stuck = {
  readonly node: HTMLElement;
  /** Où il cesse de suivre la tête, en pixels de frise. */
  readonly release: number;
  readonly centerY: boolean;
};

/**
 * Le moteur. Un par rail, monté sur deux nœuds que React lui prête.
 *
 * Il ne connaît ni React, ni le studio, ni la frise : on lui donne une gravure
 * et un tick, il en fait des pixels.
 */
export class ScoreEngine {
  private readonly legendHost: HTMLElement;

  private readonly staffLayer: HTMLElement;
  private readonly ribbon: HTMLElement;
  private readonly litClip: HTMLElement;
  private readonly litRibbon: HTMLElement;
  private readonly overRibbon: HTMLElement;
  private readonly headLayer: HTMLElement;
  private readonly glowLayer: HTMLElement;
  private readonly stuckLayer: HTMLElement;

  private layout: ScoreLayout | null = null;
  private noteNames = true;

  /** Ce qui est actuellement dans le document, et le nœud de chacun. */
  private readonly bound = new Map<ScoreItem, HTMLElement>();
  /** Le double allumé de chaque note, dans le ruban qui vit derrière la tête. */
  private readonly litBound = new Map<NoteItem, HTMLElement>();
  /** Les nœuds rendus, prêts à resservir, rangés par espèce. */
  private readonly pools = new Map<string, HTMLElement[]>();
  /** Le tampon de la fenêtre, réutilisé pour qu'un changement n'alloue pas. */
  private readonly windowed = new Set<ScoreItem>();

  /** L'abscisse autour de laquelle la fenêtre courante a été gravée. */
  private anchorPx = Number.NaN;
  private headPx = Number.NaN;
  private headTick = Number.NaN;

  /**
   * De combien la tête peut s'éloigner de l'ancre avant qu'on refasse.
   *
   * Relu à chaque recomposition et non à chaque image : `isJanky()` copie et
   * trie deux cent quarante échantillons, ce qui coûterait à la boucle plus que
   * tout ce que ce champ économise.
   */
  private reflowAt = REFLOW_PX;

  /** Le nom qui vaut, par famille, et le nœud collé qui le dessine. */
  private readonly active: Record<LabelItem["family"], LabelItem | null> = perFamily(() => null);
  private readonly stuck: Record<LabelItem["family"], Stuck | null> = perFamily(() => null);

  /**
   * Les noms qui se collent, par famille, dans l'ordre du temps.
   *
   * Eux seuls peuvent valoir « maintenant » : un compte de tours annote la
   * figure au lieu de la nommer, et un retour à la tonalité du morceau se lit
   * en passant. Les garder à part rend la recherche dichotomique juste — un
   * repère intercalé fausserait le « dernier commencé avant la tête ».
   */
  private sticky: Record<LabelItem["family"], readonly LabelItem[]> = perFamily(() => []);

  /** Les touches tenues, et le segment sous la tête, qui décident des lueurs. */
  private pressed: ReadonlySet<number> = new Set();
  private segments: readonly (FriezeSegment | undefined)[] = [];
  private glowDirty = true;

  /** Les lueurs telles qu'elles sont peintes, pour ne pas les refaire pour rien. */
  private glowKey = "";

  /**
   * Ce que la partition dit au lecteur d'écran : ce qui est sous la tête,
   * maintenant.
   *
   * Le seul chemin qui remonte du moteur vers React, et il ne porte qu'une
   * phrase. Il n'est appelé qu'au changement de nom — deux fois par mesure au
   * plus — et le rendu qu'il provoque ne touche qu'un nœud de texte : la
   * gravure est mémorisée, et le moteur n'est pas remonté.
   */
  onSpoken: ((text: string) => void) | null = null;

  constructor(frame: HTMLElement, legendHost: HTMLElement, staffHost: HTMLElement) {
    this.legendHost = legendHost;

    /*
      Le fondu des deux bords, et le rognage des côtés, d'un seul geste.

      Trois fois la hauteur et centré, pour laisser passer ce qui déborde de la
      boîte : hors de la boîte du masque, tout serait effacé. Horizontalement il
      fait exactement la largeur du cadre, ce qui rogne les côtés à la place
      d'un `clip-path`.
    */
    frame.style.position = "relative";
    frame.style.maskImage = FADE_MASK;
    frame.style.webkitMaskImage = FADE_MASK;
    frame.style.maskRepeat = "no-repeat";
    frame.style.webkitMaskRepeat = "no-repeat";
    frame.style.maskSize = "100% 300%";
    frame.style.webkitMaskSize = "100% 300%";
    frame.style.maskPosition = "center";
    frame.style.webkitMaskPosition = "center";

    /*
      Les lignes vivent hors du cadre, sous la marge des clés comme sous les
      notes : le fondu gauche du cadre les faisait commencer après les clés.
      Elles gardent le fondu de droite, pour s'éteindre avec la partition.
    */
    this.staffLayer = staffHost;
    this.staffLayer.style.maskImage = STAFF_FADE_MASK;
    this.staffLayer.style.webkitMaskImage = STAFF_FADE_MASK;
    this.ribbon = layer();
    this.litClip = layer();
    this.litRibbon = layer();
    this.overRibbon = layer();
    this.headLayer = layer();
    this.glowLayer = document.createElement("div");
    this.stuckLayer = document.createElement("div");

    // Le ruban et la couche de tête sont clouées au premier quart, en largeur
    // nulle : un tick posé à `x(t)` et un ruban translaté de `-x(t)` tombent
    // donc exactement sous la tête, sans qu'on ait jamais à mesurer le rail.
    for (const node of [this.ribbon, this.overRibbon, this.headLayer]) {
      node.style.left = `${HEAD_AT * 100}%`;
      node.style.right = "auto";
      node.style.width = "0";
    }

    /*
      La fenêtre de l'allumage : de la marge gauche à la tête de lecture, et
      pas un pixel de plus. Elle ne bouge **jamais**, puisque la tête est
      clouée au premier quart — c'est toute l'idée. Voir `litRibbon`.
    */
    this.litClip.style.right = "auto";
    this.litClip.style.width = `${HEAD_AT * 100}%`;
    this.litClip.style.overflow = "clip";

    // Le double allumé, calé sur la tête : dans une boîte qui s'arrête à elle,
    // `100 %` est exactement son abscisse.
    this.litRibbon.style.left = "100%";
    this.litRibbon.style.right = "auto";
    this.litRibbon.style.width = "0";

    // La promotion, qui est tout l'enjeu : le compositeur garde la couche en
    // texture et ne fait qu'en changer la matrice. Sans elle, une translation
    // 2D est repeinte à chaque image.
    for (const node of [this.ribbon, this.litRibbon, this.overRibbon]) {
      node.style.willChange = "transform";
      node.style.transform = "translate3d(0,0,0)";
    }

    this.litClip.append(this.litRibbon);

    /*
      Les lueurs dans une boîte à leur mesure, et non pleine tête.

      Une lueur naît et meurt quand le doigt se pose et se lève, et refaire ses
      nœuds invalide la couche qui les porte. Bornée à la colonne qu'elles
      occupent — deux fois la note, de part et d'autre de la tête — le dégât ne
      vaut plus que cette colonne, au lieu des onze cents pixels de la tête.
    */
    this.glowLayer.style.position = "absolute";
    this.glowLayer.style.left = `${-GLOW_COLUMN_PX / 2}px`;
    this.glowLayer.style.top = "0";
    this.glowLayer.style.bottom = "0";
    this.glowLayer.style.width = `${GLOW_COLUMN_PX}px`;
    this.stuckLayer.style.position = "absolute";
    this.stuckLayer.style.inset = "0";

    // Les lueurs par-dessus la tête de lecture, et non dessous : c'est la note
    // qu'on tient qu'on regarde, la ligne n'est là que pour la situer.
    this.headLayer.append(this.stuckLayer, this.glowLayer);

    frame.append(this.ribbon, this.litClip, this.overRibbon, this.headLayer);
  }

  /**
   * Poser une gravure, ou la retirer.
   *
   * Tout ce qui ne bouge pas est peint ici : les dix lignes, la clé et
   * l'armure, la tête de lecture. Le reste attend la première image.
   */
  setLayout(layout: ScoreLayout | null): void {
    this.layout = layout;
    this.reset();

    if (layout === null) {
      return;
    }

    for (const line of layout.staffLines) {
      const rule = document.createElement("div");

      rule.style.cssText = "position:absolute;left:0;right:0;height:1px";
      rule.style.top = `${line.top}px`;
      rule.style.background = STAFF_LINE;
      this.staffLayer.append(rule);
    }

    for (const item of layout.legend) {
      this.legendHost.append(paintSign(item));
    }

    this.headLayer.prepend(...headMarks());
    this.segments = layout.metrics.staves.map(() => undefined);
    this.sticky = perFamily((family) => layout.labels[family].filter(collides));
  }

  /** Écrit-on le nom des touches sur la partition ? Voir `useNoteNamesOnScore`. */
  setNoteNames(on: boolean): void {
    if (on === this.noteNames) {
      return;
    }

    this.noteNames = on;
    // La fenêtre entière est refaite : la famille des plaques y entre ou en
    // sort d'un bloc, et c'est un geste de réglage, pas une image de jeu.
    this.anchorPx = Number.NaN;

    if (!on) {
      this.dropStuck("bass");
    }
  }

  /** Les touches enfoncées, sur le vrai clavier. */
  setPressed(pressed: ReadonlySet<number>): void {
    this.pressed = pressed;
    this.glowDirty = true;
    this.paintGlows();
  }

  /**
   * Une image : la position, ce qu'elle allume, et ce qu'elle fait entrer.
   *
   * Dans cet ordre, et il n'y a que des écritures. Le cas courant — la tête a
   * bougé de quelques pixels sans franchir quoi que ce soit — coûte une
   * affectation de `transform` et trois comparaisons.
   */
  frame(tick: number): void {
    const layout = this.layout;

    if (layout === null || layout.zoom <= 0) {
      return;
    }

    const headPx = (tick - layout.fromTick) * layout.zoom;

    if (Math.abs(headPx - this.headPx) < MOVE_EPSILON_PX && this.headTick === tick) {
      return;
    }

    this.headPx = headPx;
    this.headTick = tick;

    // Les deux rubans partent de la tête de lecture, et reçoivent la même
    // matrice : c'est ce qui fait que le double allumé tombe exactement sur son
    // original. `translate3d` et non `translateX` : la forme 3D garantit la
    // couche même là où l'heuristique de promotion hésiterait.
    const matrix = `translate3d(${-headPx}px,0,0)`;

    this.ribbon.style.transform = matrix;
    this.litRibbon.style.transform = matrix;
    this.overRibbon.style.transform = matrix;

    this.labelSweep(tick);
    this.segmentSweep(tick);

    for (const family of FAMILIES) {
      placeStuck(this.stuck[family], headPx);
    }

    if (!(Math.abs(headPx - this.anchorPx) < this.reflowAt)) {
      this.reflow(headPx, tick);
    }
  }

  /** Rendre les nœuds et oublier la gravure. */
  destroy(): void {
    this.reset();
    this.staffLayer.replaceChildren();
    this.legendHost.replaceChildren();
  }

  // ── Ce qui change à des instants nommés ────────────────────────

  /**
   * Quel nom est collé à la tête, par famille.
   *
   * Un nom vaut de sa frappe jusqu'au suivant, étendue fermée à gauche et
   * ouverte à droite : à l'instant précis où un nom s'éteint, c'est le suivant
   * qui vaut, et non les deux. Voir `activeLabel`.
   *
   * Le changement est rare — deux fois par mesure au plus — et c'est le seul
   * endroit du moteur qui mesure quoi que ce soit.
   */
  private labelSweep(tick: number): void {
    let spoke = false;

    for (const family of FAMILIES) {
      const found = this.activeIn(family, tick);

      if (found === this.active[family]) {
        continue;
      }

      // Seuls les noms des deux mains se disent : une tonalité et un signe
      // d'octave se lisent, ils ne se jouent pas.
      spoke = spoke || family === "chord" || family === "bass";

      const left = this.active[family];

      this.active[family] = found;
      this.dropStuck(family);

      // Celui qui part reprend sa place dans le ruban, celui qui arrive la
      // quitte : un nom ne peut pas être écrit deux fois. Et il la reprend
      // **ici**, sans attendre la prochaine recomposition — qui peut être à
      // quatre temps de là, ce qui laisserait un trou à l'endroit exact où
      // l'œil vient de lâcher le nom.
      if (left !== null) {
        this.syncLabel(left, tick);
      }

      if (found !== null) {
        this.syncLabel(found, tick);
        this.raiseStuck(found);
      }
    }

    if (spoke) {
      this.onSpoken?.(spoken(this.active.bass, this.active.chord));
    }
  }

  /**
   * Le nom d'une famille qui vaut à cet instant, ou `null`.
   *
   * Par dichotomie sur les seuls noms qui se collent : un balayage par image
   * ferait dépendre le coût d'une image du nombre d'accords du niveau, ce que
   * tout ce fichier existe pour éviter.
   *
   * Étendue fermée à gauche et ouverte à droite : à l'instant précis où un nom
   * s'éteint, c'est le suivant qui vaut, et non les deux.
   */
  private activeIn(family: LabelItem["family"], tick: number): LabelItem | null {
    if (family === "bass" && !this.noteNames) {
      return null;
    }

    const labels = this.sticky[family];
    const at = lastStartedAt(labels, tick);
    const found = at < 0 ? undefined : labels[at];

    return found !== undefined && tick < found.untilTick ? found : null;
  }

  /**
   * Le segment sous la tête, par portée : c'est lui qui donne l'orthographe des
   * lueurs.
   *
   * Les portes sont l'union des frappes des deux mains, et une frappe ouvre
   * toujours une porte : entre deux portes, aucune note ne commence ni ne
   * s'arrête. Ce qui sonne y est donc constant, et se lit sur la porte de
   * départ — ce qui permet de poser les lueurs dans le cadre, à côté de la
   * tête, plutôt que de les animer avec le ruban.
   */
  private segmentSweep(tick: number): void {
    const staves = this.layout!.metrics.staves;
    let changed = false;

    for (let rank = 0; rank < staves.length; rank += 1) {
      const held = this.segments[rank];

      if (held !== undefined && held.fromTick <= tick && tick < held.toTick) {
        continue;
      }

      changed = true;
      break;
    }

    // Le cas courant est « rien n'a changé », et il ne doit rien allouer : la
    // reconstruction du tableau n'a lieu qu'aux portes.
    if (!changed) {
      return;
    }

    this.segments = staves.map((stave) => {
      const at = lastStartedAt(stave.segments, tick);
      const found = at < 0 ? undefined : stave.segments[at];

      return found !== undefined && tick < found.toTick ? found : undefined;
    });

    this.glowDirty = true;
    this.paintGlows();
  }

  // ── La fenêtre ────────────────────────────────────────────────

  /**
   * Refaire ce qui est dans le document, autour de la tête.
   *
   * Le coût est celui de la différence — ce qui entre et ce qui sort — et non
   * celui de la fenêtre. Entre deux appels, le ruban ne fait que glisser.
   *
   * Les mesures des noms sont faites en deux passes : tous les nœuds sont
   * d'abord posés, puis lus. Entrelacées, elles forceraient un calcul de mise
   * en page par nom.
   */
  private reflow(headPx: number, tick: number): void {
    const layout = this.layout!;

    this.anchorPx = headPx;
    // Relu ici, une fois par recomposition : voir `reflowAt`.
    this.reflowAt = getFpsWatchdog().isJanky() ? REFLOW_JANKY_PX : REFLOW_PX;

    const wanted = itemsIn(layout, headPx - BEHIND_VIEW_PX, headPx + AHEAD_VIEW_PX, this.windowed);

    for (const [item, node] of this.bound) {
      if (wanted.has(item) && this.wants(item)) {
        continue;
      }

      node.remove();
      this.bound.delete(item);
      this.recycle(item, node);

      if (item.kind === "note") {
        const twin = this.litBound.get(item);

        if (twin !== undefined) {
          twin.remove();
          this.litBound.delete(item);
          this.recycle(item, twin);
        }
      }
    }

    const measured: LabelItem[] = [];

    for (const item of wanted) {
      if (this.bound.has(item) || !this.wants(item)) {
        continue;
      }

      const node = this.take(item, tick);

      this.bound.set(item, node);
      this.ribbonFor(item).append(node);

      if (item.kind === "note") {
        const twin = this.take(item, tick, true);

        this.litBound.set(item, twin);
        this.litRibbon.append(twin);
      }

      if (item.kind === "label") {
        measured.push(item);
      }
    }

    // Seconde passe : on ne lit qu'ici, une fois tout écrit.
    for (const item of measured) {
      this.trim(item, stateOf(item, tick));
    }
  }

  /**
   * La couche où cet objet se monte.
   *
   * Le rang décide, et non l'espèce : au-delà de l'encre, l'objet doit passer
   * **au-dessus du ruban allumé**, sinon la copie allumée d'une note passerait
   * devant la plaque qui la nomme. C'est exactement ce qui se voyait — « Si »
   * et « Ré » derrière leurs notes.
   */
  private ribbonFor(item: ScoreItem): HTMLElement {
    return item.rank <= INK_MAX ? this.ribbon : this.overRibbon;
  }

  /** Cet objet a-t-il sa place dans le document, réglages compris ? */
  private wants(item: ScoreItem): boolean {
    if (item.kind !== "label") {
      return true;
    }

    if (item.family === "bass" && !this.noteNames) {
      return false;
    }

    // Celui qui est collé à la tête est dessiné par la couche fixe : le ruban
    // le tait, sans quoi il serait écrit deux fois.
    return this.active[item.family] !== item;
  }

  /**
   * Un nom qui ne tient pas dans sa fenêtre ne s'écrit pas.
   *
   * Ce qui cède se cède dans l'ordre : d'abord la **position**, qui précise le
   * geste ; puis le nom entier, faute de mieux. « Sol mineur » sans sa position
   * reste vrai ; une position sans son accord ne veut rien dire. La partition,
   * elle, garde ses notes, qui tiennent toujours.
   *
   * On ne perd pas l'accord pour autant : celui qu'on joue est collé à la tête
   * et n'est jamais caché.
   */
  private trim(item: LabelItem, state: LabelState): void {
    const node = this.bound.get(item);

    if (node === undefined) {
      return;
    }

    // Une tonalité rejetée à la fin de sa plage ne se juge plus à la place que
    // cette plage lui laissait : elle est passée, elle s'en va avec le ruban,
    // et rien ne vient plus se poser dessus.
    const room = state === "passed" && item.family === "key" ? Infinity : item.roomPx;

    if (room === Infinity) {
      return;
    }

    const cut = node.querySelector<HTMLElement>("[data-trim]");

    if (node.offsetWidth <= room) {
      return;
    }

    if (cut !== null) {
      cut.style.display = "none";

      if (node.offsetWidth <= room) {
        return;
      }
    }

    node.style.visibility = "hidden";
  }

  // ── Les nœuds : en prendre un, le rendre ──────────────────────

  /**
   * Un nœud pour cet objet, repris au tas quand il y en a un.
   *
   * Les primitives se recyclent — un rectangle est un rectangle, et le seul
   * coût évité est celui de sa création. Les noms, non : leur contenu dépend de
   * ce qu'ils disent, et remplacer le texte d'un arbre revient au même que de
   * le refaire.
   */
  private take(item: ScoreItem, tick: number, lit = false): HTMLElement {
    const held = this.pools.get(item.kind)?.pop();
    const node = held ?? create(item);

    /*
      Le rang est écrit sur le nœud, et c'est indispensable : la fenêtre monte
      ses objets colonne par colonne, donc l'ordre du document n'est pas celui
      de la gravure. Sans lui, une barre de mesure entrée après une note lui
      passerait devant.
    */
    switch (item.kind) {
      case "rule":
        node.style.cssText = "position:absolute;width:1px";
        node.style.left = `${item.left}px`;
        node.style.top = `${item.top}px`;
        node.style.height = `${item.height}px`;
        node.style.background = item.color;
        break;

      case "ledger":
        node.style.cssText = "position:absolute;height:1px";
        node.style.left = `${item.left}px`;
        node.style.top = `${item.top}px`;
        node.style.width = `${item.width}px`;
        node.style.background = item.color;
        break;

      case "octave":
        // Les tirets sont un dégradé et non une bordure pointillée : leur
        // longueur ne dépend alors d'aucun navigateur. Le crochet est la
        // bordure droite, sur toute la hauteur de la boîte.
        node.style.cssText = "position:absolute;box-sizing:border-box";
        node.style.left = `${item.left}px`;
        node.style.top = `${item.top}px`;
        node.style.width = `${item.width}px`;
        node.style.height = `${item.height}px`;
        node.style.background =
          `repeating-linear-gradient(90deg, ${item.color} 0 ${OTTAVA_DASH_PX}px, transparent ${OTTAVA_DASH_PX}px ${OTTAVA_DASH_PX + OTTAVA_GAP_PX}px)` +
          ` ${item.up ? "bottom" : "top"} / 100% 1px no-repeat`;
        node.style.borderRight = `1px solid ${item.color}`;
        break;

      case "note":
        node.style.cssText = "position:absolute";
        node.style.left = `${item.left}px`;
        node.style.top = `${item.top}px`;
        node.style.width = `${item.width}px`;
        node.style.height = `${item.height}px`;
        node.style.borderRadius = `${item.radius}px`;
        node.style.background = lit ? item.litColor : item.color;
        break;

      case "beat":
        node.style.cssText = "position:absolute;border-radius:50%";
        node.style.left = `${item.left}px`;
        node.style.top = `${item.top}px`;
        node.style.width = `${item.size}px`;
        node.style.height = `${item.size}px`;
        node.style.background = item.color;
        break;

      case "measure":
        // Sans interligne : le rail se compte au pixel, et une ligne
        // « normale » ajoutait cinq pixels de vide sous le dernier chiffre.
        node.style.cssText = `position:absolute;font-family:${FONT_MONO};font-size:10px;line-height:1`;
        node.style.left = `${item.left}px`;
        node.style.top = `${item.top}px`;
        node.style.color = TEXT_MUTE;
        node.textContent = item.text;
        break;

      case "sign":
        applySign(node as unknown as SVGSVGElement, item);
        break;

      case "label":
        return this.labelNode(item, tick);
    }

    node.style.zIndex = String(item.rank);

    return node;
  }

  /** Un nom, monté et placé selon l'état où la tête le trouve. */
  private labelNode(item: LabelItem, tick: number): HTMLElement {
    const node = document.createElement("div");

    node.setAttribute("aria-hidden", "true");
    node.style.position = "absolute";
    node.style.zIndex = String(item.rank);
    node.style.top = `${item.top}px`;
    node.append(centred(paintLabel(item, false), item.centerY));
    anchor(node, item, stateOf(item, tick));

    return node;
  }

  /**
   * Remettre un nom d'accord dans l'état où la tête le trouve.
   *
   * Monte, démonte ou déplace, selon. C'est le seul chemin qui touche au ruban
   * hors d'une recomposition, et il ne concerne jamais plus de deux nœuds : le
   * nom qui vient d'être lâché par la tête, et celui qu'elle vient de prendre.
   */
  private syncLabel(item: LabelItem, tick: number): void {
    const node = this.bound.get(item);

    if (!this.wants(item)) {
      if (node !== undefined) {
        node.remove();
        this.bound.delete(item);
      }

      return;
    }

    const state = stateOf(item, tick);

    if (node === undefined) {
      if (!this.windowed.has(item)) {
        return;
      }

      const fresh = this.labelNode(item, tick);

      this.bound.set(item, fresh);
      this.ribbonFor(item).append(fresh);
      this.trim(item, state);

      return;
    }

    // Remis à plat d'abord : un nom rogné une fois le resterait, alors que la
    // place qu'on lui accorde change avec l'état.
    node.style.visibility = "";
    node.querySelector<HTMLElement>("[data-trim]")?.style.removeProperty("display");
    anchor(node, item, state);
    this.trim(item, state);
  }

  private recycle(item: ScoreItem, node: HTMLElement): void {
    if (item.kind === "label") {
      return;
    }

    const pool = this.pools.get(item.kind) ?? [];

    // Borné : un tas qui grandirait avec le morceau finirait par tenir tout le
    // niveau en mémoire, ce que la fenêtre existe précisément pour éviter.
    if (pool.length < POOL_MAX) {
      node.style.visibility = "";
      pool.push(node);
      this.pools.set(item.kind, pool);
    }
  }

  // ── La couche de tête ─────────────────────────────────────────

  /**
   * Monter le nom collé à la tête, et mesurer où il la lâchera.
   *
   * C'est le seul endroit du rail qui ait besoin de la largeur d'un texte, et
   * c'est pour ça que le calcul est ici et non dans la frise : jusqu'où un nom
   * peut aller dépend de la place qu'il prend, ce qu'aucun module pur ne sait.
   */
  private raiseStuck(item: LabelItem): void {
    const node = document.createElement("div");

    node.setAttribute("aria-hidden", "true");
    node.style.position = "absolute";
    node.style.left = `${NAME_OFFSET_PX}px`;
    node.style.top = `${item.top}px`;
    node.append(paintLabel(item, true));

    this.stuckLayer.append(node);

    const untilPx = item.passedRight === null ? 0 : -item.passedRight + NAME_TAIL_PX;

    this.stuck[item.family] = {
      node,
      release: untilPx - NAME_TAIL_PX - node.offsetWidth - NAME_OFFSET_PX,
      centerY: item.centerY,
    };

    placeStuck(this.stuck[item.family], this.headPx);
  }

  private dropStuck(family: LabelItem["family"]): void {
    this.stuck[family]?.node.remove();
    this.stuck[family] = null;
  }

  /**
   * Les lueurs des touches tenues, à l'aplomb de la tête.
   *
   * ## Toutes les touches, pas seulement celles qu'on attend
   *
   * Une touche que la partition ne demande pas ici s'éclaire aussi, à sa
   * hauteur. C'est ce qui en fait un retour et non une correction : le joueur
   * voit **ce qu'il fait**, et il voit du même coup que ce n'est pas à la
   * hauteur du trait. N'éclairer que le juste ferait de l'absence de lueur la
   * seule façon d'apprendre qu'on s'est trompé, c'est-à-dire un jugement rendu
   * par le silence.
   *
   * ## Où se pose une touche que la partition n'écrit pas
   *
   * Sur sa portée si elle y est écrite, ce qui règle le cas courant sans rien
   * décider. Sinon, elle est écrite dans l'orthographe de la fenêtre — `si♭`
   * sous un accord de sol mineur, jamais `la♯` — et posée de part et d'autre du
   * do central, comme sur n'importe quelle partition de piano.
   */
  private paintGlows(): void {
    const layout = this.layout;

    if (layout === null || !this.glowDirty) {
      return;
    }

    this.glowDirty = false;

    if (this.pressed.size === 0) {
      if (this.glowKey !== "") {
        this.glowKey = "";
        this.glowLayer.replaceChildren();
      }

      return;
    }

    const metrics = layout.metrics;
    // Réglée sur la note et non sur un chiffre à elle : la portée se resserre
    // quand l'ambitus s'élargit, et la lueur doit rester à sa mesure.
    const size = metrics.noteHeight * SOUND_DOT_SPREAD;
    // Les deux mains d'un même instant viennent de la même fenêtre, donc de la
    // même orthographe : la première qui joue suffit à la donner.
    const spelling = this.segments.find((segment) => segment !== undefined);

    const places: number[] = [];

    for (const midi of this.pressed) {
      const place = this.placeOf(midi, spelling, size);

      if (place !== null) {
        places.push(place);
      }
    }

    /*
      La porte suivante ne déplace presque jamais une lueur : la main tient les
      mêmes touches, et une touche tenue garde sa hauteur tant que
      l'orthographe de la fenêtre ne change pas. Refaire les nœuds à chaque
      porte revenait donc à repeindre quatre fois par seconde un dessin
      identique — ce qui était, sur le banc, la totalité de ce qui restait à
      peindre une fois le ruban composé.

      On compare donc le **résultat** et non ses causes : c'est la seule
      comparaison qui ne peut pas se tromper, puisqu'elle porte exactement sur
      ce qui serait dessiné.
    */
    const key = places.join(",");

    if (key === this.glowKey) {
      return;
    }

    this.glowKey = key;
    this.glowLayer.replaceChildren();

    for (const place of places) {
      const dot = document.createElement("div");

      dot.style.cssText = "position:absolute";
      dot.style.left = `${(GLOW_COLUMN_PX - size) / 2}px`;
      dot.style.top = `${place}px`;
      dot.style.width = `${size}px`;
      dot.style.height = `${size}px`;
      dot.style.background = SOUND_DOT_LIGHT;
      this.glowLayer.append(dot);
    }
  }

  private placeOf(midi: number, spelling: FriezeSegment | undefined, size: number): number | null {
    const metrics = this.layout!.metrics;
    let written: { stave: StavePlace; step: number } | null = null;

    metrics.staves.forEach((stave, rank) => {
      const key = this.segments[rank]?.keys.find((at) => at.midi === midi);

      if (key !== undefined && written === null) {
        written = { stave, step: key.step };
      }
    });

    if (written !== null) {
      const place: { stave: StavePlace; step: number } = written;

      return stepMiddle(metrics, place.stave, place.step) - size / 2;
    }

    if (spelling === undefined) {
      return null;
    }

    const step = spellStaffKey({
      midi,
      spelled: spelling.spelling?.get(midi) ?? null,
      rootSpelling: spelling.rootSpelling,
      rootPitchClass: spelling.rootPitchClass,
      chordPitchClasses: spelling.chordPitchClasses,
      keyTonic: metrics.keyTonic,
    }).step;
    const stave = staveForStep(metrics, step);

    // Sous un 8va, la portée écrit une octave plus bas qu'elle ne sonne : une
    // touche posée à sa hauteur jouée y mentirait d'une octave.
    return stepMiddle(metrics, stave, writtenStep(step, shiftAt(stave.ottavas, this.headTick))) - size / 2;
  }

  /** Tout rendre : les nœuds du ruban, les noms collés, les lueurs. */
  private reset(): void {
    this.ribbon.replaceChildren();
    this.litRibbon.replaceChildren();
    this.overRibbon.replaceChildren();
    this.glowLayer.replaceChildren();
    this.stuckLayer.replaceChildren();
    this.staffLayer.replaceChildren();
    this.legendHost.replaceChildren();

    for (const mark of [...this.headLayer.children]) {
      if (mark !== this.glowLayer && mark !== this.stuckLayer) {
        mark.remove();
      }
    }

    this.bound.clear();
    this.litBound.clear();
    this.pools.clear();
    this.windowed.clear();

    for (const family of FAMILIES) {
      this.active[family] = null;
      this.stuck[family] = null;
    }

    this.anchorPx = Number.NaN;
    this.headPx = Number.NaN;
    this.headTick = Number.NaN;
    this.segments = [];
    this.glowKey = "";
    this.sticky = perFamily(() => []);
    this.glowDirty = true;
  }
}

const FAMILIES = ["chord", "bass", "key", "ottavaRight", "ottavaLeft"] as const satisfies readonly LabelItem["family"][];

/** Une valeur par famille de noms, faite par `make`. */
function perFamily<T>(make: (family: LabelItem["family"]) => T): Record<LabelItem["family"], T> {
  return {
    chord: make("chord"),
    bass: make("bass"),
    key: make("key"),
    ottavaRight: make("ottavaRight"),
    ottavaLeft: make("ottavaLeft"),
  };
}

/** Le plafond du tas, par espèce : de quoi tenir une fenêtre, pas un morceau. */
const POOL_MAX = 256;

/** Un nom qui se colle à la tête, par opposition à un repère qui ne bouge pas. */
function collides(item: LabelItem): boolean {
  return item.passedRight !== null;
}

/** Ce que la partition dit au lecteur d'écran : ce qui est sous la tête, maintenant. */
function spoken(bass: LabelItem | null, chord: LabelItem | null): string {
  return [
    bass === null ? null : `Main gauche : ${nameOf(bass)}`,
    chord === null ? null : `Main droite : ${nameOf(chord)}`,
  ]
    .filter((part) => part !== null)
    .join(". ");
}

function nameOf(item: LabelItem): string {
  return item.text.name;
}

/**
 * Le dernier élément commencé au plus tard à cet instant, ou `-1`.
 *
 * Par dichotomie, et c'est ce qui rend le coût d'une image indépendant de la
 * longueur du morceau : une frise de niveau tient un temps par temps du
 * morceau, et on l'interroge à chaque image.
 */
export function lastStartedAt(items: readonly { readonly fromTick: number }[], tick: number): number {
  let low = 0;
  let high = items.length - 1;
  let found = -1;

  while (low <= high) {
    const middle = (low + high) >> 1;

    if (items[middle]!.fromTick <= tick) {
      found = middle;
      low = middle + 1;
    } else {
      high = middle - 1;
    }
  }

  return found;
}

/**
 * Où un nom se pose, selon là où la tête en est.
 *
 * Exportée pour le banc : c'est la règle des trois vies d'un nom, et elle se
 * vérifie sans navigateur alors que tout ce qui l'entoure demande un écran.
 *
 * Devant elle, il attend à sa frappe, calé par son bord gauche. Passé, il se
 * cale sur la fin de son étendue, par son bord **droit** : entre-temps il a
 * suivi la tête et l'a quittée au moment où il ne tenait plus dans son étendue,
 * et le rendre à sa frappe le ferait sauter en arrière de toute la largeur de
 * la fenêtre.
 */
export function stateOf(item: LabelItem, tick: number): LabelState {
  if (item.passedRight === null) {
    return "ahead";
  }

  if (tick >= item.untilTick) {
    return "passed";
  }

  return tick >= item.fromTick ? "stuck" : "ahead";
}

function anchor(node: HTMLElement, item: LabelItem, state: LabelState): void {
  if (state === "passed" && item.passedRight !== null) {
    node.style.left = "auto";
    node.style.right = `${item.passedRight}px`;

    return;
  }

  node.style.right = "auto";
  node.style.left = `${item.aheadLeft}px`;
}

/**
 * Poser un nom collé, pour cette image.
 *
 * `Math.min(0, …)` est tout le collage : tant que la tête n'a pas atteint
 * l'abscisse de relâchement, le nom reste sur elle ; après, il s'en écarte
 * exactement de ce que la tête a parcouru en trop, donc il paraît immobile
 * dans le ruban.
 */
function placeStuck(stuck: Stuck | null, headPx: number): void {
  if (stuck === null) {
    return;
  }

  const shift = Math.min(0, stuck.release - headPx);

  stuck.node.style.transform = `translate3d(${shift}px,${stuck.centerY ? "-50%" : "0"},0)`;
}

/**
 * La boîte intérieure d'un nom du ruban.
 *
 * La verticale est portée par elle, pour que l'extérieure garde son ancrage
 * libre : c'est ce qui permet de caler un nom par la droite sans perdre son
 * centrage sur la ligne.
 */
function centred(inner: HTMLElement, centerY: boolean): HTMLElement {
  const box = document.createElement("div");

  if (centerY) {
    box.style.transform = "translateY(-50%)";
  }

  box.append(inner);

  return box;
}

/**
 * Une couche du cadre : pleine boîte, muette, et transparente au doigt.
 *
 * `pointer-events: none` parce que le geste appartient à la surface, un cran
 * plus haut : c'est elle qui conduit la navigation du studio, et une couche qui
 * répondrait au test de survol ferait payer une recherche de cible par
 * mouvement de doigt, pour un événement qu'elle relaierait de toute façon.
 */
function layer(): HTMLElement {
  const node = document.createElement("div");

  node.setAttribute("aria-hidden", "true");
  node.style.cssText = "position:absolute;left:0;right:0;top:0;bottom:0;pointer-events:none";

  return node;
}

/**
 * La tête de lecture : le cheveu, et ses deux pointes.
 *
 * Dessinées en bordures plutôt qu'en glyphe : un triangle de six pixels de côté
 * est net au pixel, là où un caractère tombe dans la fonte de secours et arrive
 * plus maigre que ses voisins.
 */
function headMarks(): readonly HTMLElement[] {
  const line = document.createElement("div");

  line.style.cssText = "position:absolute;top:0;height:100%";
  line.style.left = `${-HEAD_WIDTH_PX / 2}px`;
  line.style.width = `${HEAD_WIDTH_PX}px`;
  line.style.background = HEAD_LINE;

  const tips = (["top", "bottom"] as const).map((edge) => {
    const tip = document.createElement("div");

    tip.style.cssText = "position:absolute;width:0;height:0";
    tip.style.left = `${-HEAD_TIP_PX}px`;
    tip.style[edge] = "0";
    tip.style.borderLeft = `${HEAD_TIP_PX}px solid transparent`;
    tip.style.borderRight = `${HEAD_TIP_PX}px solid transparent`;
    tip.style[edge === "top" ? "borderTop" : "borderBottom"] = `${HEAD_TIP_PX + 1}px solid ${HEAD_TIP}`;

    return tip;
  });

  return [line, ...tips];
}

function create(item: ScoreItem): HTMLElement {
  if (item.kind === "sign") {
    return document.createElementNS("http://www.w3.org/2000/svg", "svg") as unknown as HTMLElement;
  }

  return document.createElement(item.kind === "measure" ? "span" : "div");
}

function paintSign(item: SignItem): HTMLElement {
  const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");

  applySign(svg, item);

  return svg as unknown as HTMLElement;
}

/**
 * Un signe gravé, peint et non lu.
 *
 * `fill="currentColor"` : le signe prend la couleur de sa main sans que ce
 * module ait à connaître la palette, et il est masqué aux lecteurs d'écran avec
 * le reste du rail, qui a sa propre phrase.
 */
function applySign(svg: SVGSVGElement, item: SignItem): void {
  svg.setAttribute("viewBox", item.viewBox);
  svg.setAttribute("width", `${item.width}`);
  svg.setAttribute("height", `${item.height}`);
  svg.style.cssText = "position:absolute;overflow:visible";
  svg.style.left = `${item.left}px`;
  svg.style.top = `${item.top}px`;
  svg.style.color = item.color;

  const path = svg.firstElementChild ?? svg.appendChild(document.createElementNS("http://www.w3.org/2000/svg", "path"));

  path.setAttribute("d", item.path);
  path.setAttribute("fill", "currentColor");
}

// ── Ce qu'un nom écrit ───────────────────────────────────────────

/**
 * Le corps d'un nom, quelle que soit sa vie.
 *
 * `here` est la seule différence entre le nom collé à la tête et les autres :
 * il s'éclaircit, et sa plaque se densifie. Le même arbre dans les deux cas,
 * sans quoi le passage de l'un à l'autre se verrait.
 */
function paintLabel(item: LabelItem, here: boolean): HTMLElement {
  switch (item.text.of) {
    case "chord":
      return paintChord(item.text, here);
    case "bass":
      return paintBass(item.text, here);
    case "key":
      return paintKey(item.text, here);
    case "ottava":
      return paintOttava(item.text);
  }
}

/**
 * Le chiffrage d'un accord, au-dessus de la portée de droite.
 *
 * Deux lignes, et la seconde n'est pas une redite de la première. Le nom dit
 * **quel** accord ; les notes dessous disent **dans quel ordre** la main les
 * empile, ce qui est exactement ce que la position nomme à côté du nom. On peut
 * donc lire le renversement au lieu d'avoir à croire l'étiquette, et la
 * fondamentale est teintée pour qu'on voie où elle est tombée dans la pile.
 *
 * Sans plaque, à la différence des noms de touches : rien ne passe derrière lui
 * qu'il faudrait masquer, la portée commence en dessous.
 */
function paintChord(text: Extract<LabelItem["text"], { of: "chord" }>, here: boolean): HTMLElement {
  const box = document.createElement("div");

  box.style.cssText =
    "display:flex;flex-direction:column;justify-content:flex-end;gap:2px;white-space:nowrap;" +
    "font-family:Inter, sans-serif;font-weight:300";
  box.style.height = `${CHORD_BLOCK_PX}px`;

  const line = document.createElement("span");

  line.style.cssText = "display:flex;align-items:baseline";

  const name = document.createElement("span");

  // Un cheveu plus serré que la normale, comme le grand nom du milieu de
  // l'écran : c'est le même mot, écrit deux fois plus petit, et il doit se
  // reconnaître de la même façon.
  name.style.cssText = "line-height:1;letter-spacing:-0.01em";
  name.style.fontSize = `${CHORD_NAME_PX}px`;
  name.style.color = here ? VIOLET_BRIGHT : RIGHT_HAND_COLOR;
  name.textContent = text.name;
  line.append(name);

  // La barre du symbole, dans la couleur de la main qui joue cette basse. Elle
  // fait partie du nom et ne cède donc pas avec la position.
  if (text.slash !== null) {
    const slash = document.createElement("span");

    slash.style.cssText = "line-height:1;letter-spacing:-0.01em";
    slash.style.fontSize = `${Math.round(CHORD_NAME_PX * 0.7)}px`;
    slash.style.color = LEFT_HAND_COLOR;
    slash.textContent = `/${text.slash}`;
    line.append(slash);
  }

  if (text.position !== null) {
    const position = document.createElement("span");

    // La première chose qui saute quand la fenêtre est courte : c'est une
    // précision sur le geste, le nom est l'information.
    position.setAttribute("data-trim", "");
    position.style.cssText = `margin-left:9px;font-family:${FONT_MONO};line-height:1;letter-spacing:0.1em;text-transform:uppercase`;
    position.style.fontSize = `${CHORD_POSITION_PX}px`;
    position.style.color = text.positionColor;
    position.textContent = text.position;
    line.append(position);
  }

  box.append(line);

  if (text.notes.length === 0) {
    return box;
  }

  const notes = document.createElement("span");

  notes.style.cssText = "display:flex;align-items:baseline;gap:4px;line-height:1;letter-spacing:0.04em";
  notes.style.fontSize = `${CHORD_NOTES_PX}px`;
  notes.style.color = TEXT_MUTE;

  text.notes.forEach((note, rank) => {
    const cell = document.createElement("span");

    cell.style.cssText = "display:flex;align-items:baseline;gap:4px";

    if (rank > 0) {
      // Le point médian, et non un espace : trois lettres séparées par du vide
      // se lisent comme un mot, et « D F A » n'en est pas un.
      const dot = document.createElement("span");

      dot.style.color = SEPARATOR;
      dot.textContent = "·";
      cell.append(dot);
    }

    const label = document.createElement("span");

    if (note.root) {
      label.style.color = VIOLET_BRIGHT;
    }

    label.textContent = note.name;
    cell.append(label);
    notes.append(cell);
  });

  box.append(notes);

  return box;
}

/**
 * Le nom d'une touche de la main gauche, sur sa portée, à sa hauteur.
 *
 * Sur une plaque, et opaque : il se pose sur les cinq lignes et sur ce qui se
 * joue derrière, du texte nu s'y perdrait, et un fond translucide laisserait
 * passer une ligne en travers des lettres. La plaque de celui qu'on joue est
 * plus dense et son trait plus clair, ce qui est la seule différence entre le
 * nom d'ici et ceux d'après.
 *
 * Il ne s'efface pas quand il est à venir : ce qui a été joué se lit sur les
 * notes, qui s'éclaircissent, et éteindre en plus les noms à venir supprimerait
 * la seule chose qui permet de préparer la main.
 */
function paintBass(text: Extract<LabelItem["text"], { of: "bass" }>, here: boolean): HTMLElement {
  const plate = document.createElement("span");

  plate.style.cssText = "display:flex;align-items:center;padding:2px 9px;border-radius:7px;font-weight:300;line-height:1.1;white-space:nowrap";
  plate.style.background = here ? grey(6) : "var(--color-bg-deep)";
  plate.style.outline = `1px solid ${grey(here ? 22 : 10)}`;
  plate.style.outlineOffset = "-1px";
  plate.style.fontFamily = text.loop ? FONT_MONO : "Inter, sans-serif";
  plate.style.fontSize = `${text.loop ? LOOP_SIZE_PX : BASS_NAME_PX}px`;
  plate.style.letterSpacing = text.loop ? "0.1em" : "-0.01em";
  plate.style.color = text.loop ? TEXT_MUTE : LEFT_HAND_COLOR;
  plate.textContent = text.name;

  return plate;
}

/**
 * Le nom d'une tonalité, tel qu'il s'écrit sous la règle.
 *
 * Collé à la tête, il prend une plaque de la couleur du fond, comme le nom de
 * touche de la basse : les numéros de mesure du ruban passent derrière lui et
 * ne doivent pas se lire à travers.
 */
function paintKey(text: Extract<LabelItem["text"], { of: "key" }>, here: boolean): HTMLElement {
  const name = document.createElement("span");

  // En bloc et non en ligne : posé en ligne dans un bloc, le nom s'alignerait
  // sur la ligne de base d'une boîte de ligne plus haute que lui et descendrait
  // sous la règle, où le rail s'arrête.
  name.style.cssText = `display:block;font-family:${FONT_MONO};font-size:10px;line-height:1;letter-spacing:0.14em;text-transform:uppercase;white-space:nowrap`;
  name.style.color = text.away ? VIOLET_BRIGHT : TEXT_MUTE;

  if (here) {
    name.style.background = "var(--color-bg-deep)";
    name.style.padding = "0 6px 0 4px";
    name.style.marginLeft = "-4px";
  }

  name.textContent = text.name;

  return name;
}

/**
 * Un signe d'octave, sur une plaque de la couleur du fond.
 *
 * La plaque cache les tirets qu'il recouvre : collé à la tête, il glisse sur
 * sa propre ligne, et un « 8va » barré de tirets ne se lirait plus. Le même
 * dessin devant la tête et sous elle, pour que le passage de l'un à l'autre ne
 * se voie pas.
 */
function paintOttava(text: Extract<LabelItem["text"], { of: "ottava" }>): HTMLElement {
  const plate = document.createElement("span");

  plate.style.cssText = "display:block;line-height:0;padding:0 4px;margin-left:-4px";
  plate.style.background = "var(--color-bg-deep)";

  const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");

  svg.setAttribute("viewBox", text.viewBox);
  svg.setAttribute("width", `${text.width}`);
  svg.setAttribute("height", `${text.height}`);
  svg.style.cssText = "display:block;overflow:visible";
  svg.style.color = text.color;

  const path = document.createElementNS("http://www.w3.org/2000/svg", "path");

  path.setAttribute("d", text.path);
  path.setAttribute("fill", "currentColor");
  svg.append(path);
  plate.append(svg);

  return plate;
}
